import {StCryptographyProvider} from "./StCryptographyProvider";

export class StASymmetricCryptographyProvider
    implements StCryptographyProvider {
    private readonly TRANSFORMATION_PADDING = "RSA-OAEP";

    async encrypt(data: string, base64PublicKey: string): Promise<string> {
        const publicKey = await this.importPublicKey(base64PublicKey);
        const encodedData = new TextEncoder().encode(data);
        const encryptedData = await window.crypto.subtle.encrypt(
            {
                name: this.TRANSFORMATION_PADDING,
                length: 256, // Specify the length of the RSA key here
                // Do not include the hash property directly in the encryption parameters
            },
            publicKey,
            encodedData
        );
        return this.arrayBufferToBase64(encryptedData);
    }

    async decrypt(data: string, base64PrivateKey: string): Promise<string> {
        const privateKey = await this.importPrivateKey(base64PrivateKey);
        const encryptedData = this.base64ToArrayBuffer(data);
        const decryptedData = await window.crypto.subtle.decrypt(
            {
                name: this.TRANSFORMATION_PADDING,
                // Do not include the hash property directly in the decryption parameters
            },
            privateKey,
            encryptedData
        );
        return new TextDecoder().decode(decryptedData);
    }

    private async importPublicKey(base64Key: string): Promise<CryptoKey> {
        const keyBuffer = this.base64ToArrayBuffer(base64Key);
        return await window.crypto.subtle.importKey(
            "spki",
            keyBuffer,
            {
                name: this.TRANSFORMATION_PADDING,
                hash: {name: "SHA-256"}, // Specify the hash algorithm here
            },
            true,
            ["encrypt"]
        );
    }

    private async importPrivateKey(base64Key: string): Promise<CryptoKey> {
        const pemStringWithoutHeaderAndFooter = this.stripPemHeaderAndFooter(base64Key);
        const keyBuffer = this.base64ToArrayBuffer(pemStringWithoutHeaderAndFooter);
        return await window.crypto.subtle.importKey(
            "pkcs8",
            keyBuffer,
            {
                name: this.TRANSFORMATION_PADDING,
                hash: {name: "SHA-256"}, // Specify the hash algorithm here
            },
            true,
            ["decrypt"]
        );
    }

    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    private stripPemHeaderAndFooter(pemString: string): string {
        return pemString
            .replace(/-----BEGIN [A-Z ]+-----/, "")
            .replace(/-----END [A-Z ]+-----/, "")
            .replace(/[\r\n\s]/g, "");
    }
}
